import React, { useState, useEffect } from "react";
import { db, workshops } from "@/api/db";
import { CalendarCheck, Clock, Users, MapPin, Loader2, Check, X, Info } from "lucide-react";
import { STATUS_CONFIG, CATEGORY_COLORS } from "@/lib/live-constants";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("pt-BR", {
    weekday: "long", day: "numeric", month: "long",
  });
}

function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function isPast(iso) {
  return iso && new Date(iso) < new Date();
}

export default function Workshops() {
  const [oficinas, setOficinas] = useState([]);
  const [bookings, setBookings] = useState({});
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [bookingId, setBookingId] = useState(null);
  const [showBanner, setShowBanner] = useState(() => localStorage.getItem("portus_oficinas_banner") !== "closed");

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const [allLessons, myBookings] = await Promise.all([
        db.entities.Lesson.filter({ type: "live", is_published: true, category: "Oficina" }, "live_date"),
        workshops.myBookings(),
      ]);

      const upcoming = allLessons.filter(l => l.live_status !== "recording");
      setOficinas(upcoming);

      const bookMap = {};
      myBookings.forEach(b => { bookMap[b.lesson_id] = b; });
      setBookings(bookMap);

      const availMap = {};
      await Promise.all(upcoming.map(async (o) => {
        try {
          const av = await workshops.availability(o.id);
          availMap[o.id] = av;
        } catch { /* ignore */ }
      }));
      setAvailability(availMap);
    } catch (err) {
      console.error("Erro ao carregar oficinas:", err);
    } finally {
      setLoading(false);
    }
  }

  async function handleBook(lessonId) {
    setBookingId(lessonId);
    try {
      await workshops.book(lessonId);
      await loadData();
    } catch (err) {
      alert(err.message || "Erro ao agendar");
    } finally {
      setBookingId(null);
    }
  }

  async function handleCancel(lessonId) {
    if (!confirm("Deseja cancelar seu agendamento?")) return;
    setBookingId(lessonId);
    try {
      await workshops.cancel(lessonId);
      await loadData();
    } catch (err) {
      alert(err.message || "Erro ao cancelar");
    } finally {
      setBookingId(null);
    }
  }

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <Loader2 size={32} style={{ animation: "spin 1s linear infinite", color: "#00C896" }} />
      </div>
    );
  }

  const upcoming = oficinas.filter(o => !isPast(o.live_date));
  const past = oficinas.filter(o => isPast(o.live_date));

  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: 0 }}>
          Oficinas ao Vivo
        </h1>
        <p style={{ fontSize: 14, color: "#6B7280", marginTop: 4 }}>
          Agende sua participação nas oficinas práticas. Vagas limitadas.
        </p>
      </div>

      {showBanner && (
        <div style={{
          background: "linear-gradient(135deg, #EAFAF6 0%, #D4F5ED 100%)",
          border: "1px solid #B0FCEA",
          borderRadius: 16,
          padding: "16px 20px",
          marginBottom: 24,
          display: "flex",
          gap: 14,
          alignItems: "flex-start",
        }}>
          <Info size={20} style={{ color: "#00664C", flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#003326", margin: "0 0 4px" }}>
              Como funcionam as oficinas?
            </p>
            <p style={{ fontSize: 13, color: "#00664C", lineHeight: 1.6, margin: 0 }}>
              As oficinas são encontros práticos ao vivo com vagas limitadas. Temos duas oficinas quinzenais:{" "}
              <strong>Análise de Aplicações</strong> (revisamos candidaturas reais e damos feedback) e{" "}
              <strong>Simulado de Entrevistas</strong> (praticamos entrevistas em tempo real). Agende com antecedência para garantir sua vaga e receber o link de acesso.
            </p>
          </div>
          <button
            onClick={() => {
              setShowBanner(false);
              localStorage.setItem("portus_oficinas_banner", "closed");
            }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#99A6AB", flexShrink: 0, padding: 0,
            }}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {upcoming.length === 0 && past.length === 0 && (
        <div style={{
          textAlign: "center", padding: 48, background: "#F9FAFB",
          borderRadius: 16, border: "1px solid #E5E7EB",
        }}>
          <CalendarCheck size={40} style={{ color: "#D1D5DB", margin: "0 auto 12px" }} />
          <p style={{ color: "#6B7280", fontSize: 15 }}>Nenhuma oficina agendada no momento.</p>
        </div>
      )}

      {upcoming.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: "#374151", marginBottom: 16 }}>
            Próximas oficinas
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {upcoming.map(o => (
              <WorkshopCard
                key={o.id}
                oficina={o}
                isBooked={!!bookings[o.id]}
                availability={availability[o.id]}
                isLoading={bookingId === o.id}
                onBook={() => handleBook(o.id)}
                onCancel={() => handleCancel(o.id)}
              />
            ))}
          </div>
        </div>
      )}

      {past.length > 0 && (
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: "#9CA3AF", marginBottom: 16 }}>
            Oficinas anteriores
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {past.map(o => (
              <WorkshopCard key={o.id} oficina={o} isPast availability={availability[o.id]} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WorkshopCard({ oficina, isBooked, availability: av, isLoading, onBook, onCancel, isPast }) {
  const spotsText = av?.max
    ? `${av.booked}/${av.max} vagas`
    : av ? `${av.booked} inscritos` : "";
  const isFull = av?.max && av.booked >= av.max && !isBooked;

  return (
    <div style={{
      background: isPast ? "#FAFAFA" : "#FFFFFF",
      border: isBooked ? "2px solid #00C896" : "1px solid #E5E7EB",
      borderRadius: 16,
      padding: 24,
      opacity: isPast ? 0.6 : 1,
      transition: "all 0.2s",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          {/* Title */}
          <h3 style={{ fontSize: 17, fontWeight: 600, color: "#111827", margin: "0 0 8px" }}>
            {oficina.title}
          </h3>

          {/* Description */}
          {oficina.description && (
            <p style={{ fontSize: 13, color: "#6B7280", margin: "0 0 12px", lineHeight: 1.5 }}>
              {oficina.description}
            </p>
          )}

          {/* Meta info */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 13, color: "#6B7280" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <CalendarCheck size={14} />
              {formatDate(oficina.live_date)}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Clock size={14} />
              {formatTime(oficina.live_date)}
            </span>
            {spotsText && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Users size={14} />
                {spotsText}
              </span>
            )}
          </div>
        </div>

        {/* Action */}
        {!isPast && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
            {isBooked ? (
              <>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: "#E6FAF4", color: "#00664C",
                  padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                }}>
                  <Check size={14} /> Agendado
                </span>
                {oficina.live_url && (
                  <a
                    href={oficina.live_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: "#00C896", color: "#fff",
                      padding: "8px 20px", borderRadius: 8,
                      fontSize: 13, fontWeight: 600, textDecoration: "none",
                      display: "inline-block",
                    }}
                  >
                    Acessar sala
                  </a>
                )}
                <button
                  onClick={onCancel}
                  disabled={isLoading}
                  style={{
                    background: "none", border: "none", color: "#9CA3AF",
                    fontSize: 12, cursor: "pointer", padding: 0,
                  }}
                >
                  Cancelar agendamento
                </button>
              </>
            ) : (
              <button
                onClick={onBook}
                disabled={isLoading || isFull}
                style={{
                  background: isFull ? "#E5E7EB" : "#00C896",
                  color: isFull ? "#9CA3AF" : "#fff",
                  border: "none", padding: "10px 24px", borderRadius: 10,
                  fontSize: 14, fontWeight: 600,
                  cursor: isFull ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {isLoading ? (
                  <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
                ) : isFull ? "Vagas esgotadas" : "Agendar"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
